import { z } from 'zod';

// Slug allows Thai (U+0E00-U+0E7F) + lowercase Latin + digits, separated by single dashes.
const slugRx = /^[฀-๿a-z0-9]+(?:-[฀-๿a-z0-9]+)*$/u;

export const createCategorySchema = z.object({
  name: z.string().min(1, 'กรุณาใส่ชื่อหมวด').max(100),
  slug: z.string().regex(slugRx, 'slug ใช้ได้: ไทย / a-z / 0-9 และ - เท่านั้น').max(100).optional(),
  description: z.string().max(500).optional().nullable(),
  parentId: z.string().uuid().optional().nullable(),
  /** Media row id picked from the library. `null` to clear an existing
   *  cover; `undefined` (omit) to leave the saved value untouched on PATCH. */
  coverImageId: z.string().uuid().optional().nullable(),
});

export type CreateCategoryInput = z.infer<typeof createCategorySchema>;

export const createTagSchema = z.object({
  name: z.string().min(1, 'กรุณาใส่ชื่อแท็ก').max(60),
  slug: z.string().regex(slugRx, 'slug ใช้ได้: ไทย / a-z / 0-9 และ - เท่านั้น').max(60).optional(),
});

export type CreateTagInput = z.infer<typeof createTagSchema>;

export const listTaxonomyQuerySchema = z.object({
  q: z.string().trim().min(1).max(60).optional(),
  perPage: z.coerce.number().int().positive().max(100).default(50),
});

export type ListTaxonomyQuery = z.infer<typeof listTaxonomyQuerySchema>;

export const updateCategorySchema = createCategorySchema.partial();
export type UpdateCategoryInput = z.infer<typeof updateCategorySchema>;

// One-step reorder relative to the sibling above/below — admin
// presses ↑ / ↓ instead of typing sortOrder numbers or wiring a
// drag handle. Backend swaps target with neighbor by index and
// renumbers the affected sibling group as i*10 in a single batch.
export const reorderCategorySchema = z.object({
  direction: z.enum(['up', 'down']),
});
export type ReorderCategoryInput = z.infer<typeof reorderCategorySchema>;

/**
 * Bulk reorder within one sibling group — admin drags rows in the
 * categories list. `parentId=null` means top-level. Backend verifies
 * every supplied id actually belongs to that parent (refuse cross-parent
 * moves) then renumbers their sort_order as i*10 atomically. Capped at
 * 200 — practical limit for a single parent's children.
 */
export const reorderCategoriesBulkSchema = z.object({
  parentId: z.string().uuid().nullable(),
  orderedIds: z.array(z.string().uuid()).min(1).max(200),
});
export type ReorderCategoriesBulkInput = z.infer<typeof reorderCategoriesBulkSchema>;

export const updateTagSchema = createTagSchema.partial();
export type UpdateTagInput = z.infer<typeof updateTagSchema>;

/**
 * Drag-and-drop OR ↑/↓ buttons — both UI gestures send the full
 * ordered id list and the backend rewrites every matched tag's
 * `sort_order` to its index*10. Ids the backend doesn't know about
 * are silently dropped (the UI shouldn't be able to send foreign ids,
 * but defense in depth). Cap matches playlists at 1000.
 */
export const reorderTagsSchema = z.object({
  orderedIds: z.array(z.string()).min(1).max(1000),
});
export type ReorderTagsInput = z.infer<typeof reorderTagsSchema>;

/**
 * Bulk merge — move every post_tags reference from each `fromIds`
 * tag onto `toId`, then delete the source tag rows. ON CONFLICT
 * dedupes the case where a post already has both tags. Capped at 20
 * sources/call so a misclick can't wipe a long tail in one shot.
 */
export const mergeTagsSchema = z
  .object({
    fromIds: z.array(z.string().uuid()).min(1).max(20),
    toId: z.string().uuid(),
  })
  .refine((v) => !v.fromIds.includes(v.toId), {
    message: 'toId cannot appear in fromIds',
    path: ['toId'],
  });

export type MergeTagsInput = z.infer<typeof mergeTagsSchema>;
